import { Trans, useLingui } from '@lingui/react/macro'
import type { PaginationState, SortingState } from '@tanstack/react-table'
import { Box } from 'designSystem/components/Box'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from 'designSystem/components/Resizable'
import { Skeleton } from 'designSystem/components/Skeleton'
import { DocumentPreviewPanel } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/DocumentPreviewPanel'
import { FloatingDocumentPreviewOverlay } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/FloatingDocumentPreviewOverlay'
import { useFloatingDocumentPreview } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/useFloatingDocumentPreview'
import { DocumentCardFilterBar } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/DocumentCardFilterBar'
import { DocumentResultsList } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/DocumentResultsList'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type { BrowseAllDisplayState } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import type { UseDocumentResultsInteractionsResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsInteractions'
import type { UseDocumentResultsViewStateResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsViewState'
import {
  NoResults,
  PreviewPane,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/advancedSearchStyles'
import type {
  DrawingRevisionGroup,
  Highlight,
  PageClassification,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { getDocumentTablePage } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentTablePage'
import { GeckoPowerTable } from 'features/knowledgeDocumentViewer/components/table/GeckoPowerTable'
import type { ViewMode } from 'features/knowledgeDocumentViewer/types'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { Dispatch, ReactElement, SetStateAction } from 'react'
import React, { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { DocumentResultsFooter } from './DocumentResultsFooter'
import { SearchTableWrapper } from './SearchTableWrapper'

const ResizablePanelContent = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
  min-width: 0;
`

const DocumentResultsLoadingSkeleton = memo(
  function DocumentResultsLoadingSkeleton(): ReactElement {
    const { t } = useLingui()
    return (
      <Box
        aria-label={t`Loading document results`}
        role='status'
        display='flex'
        direction='column'
        flex='one'
        minHeight='none'
        gap='sm'
        padding='sm'
        overflow='hidden'>
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton
            key={index}
            variant='ditheredPulse'
            style={{ flex: '1 1 0', minHeight: 0 }}
          />
        ))}
      </Box>
    )
  }
)

const DocumentPreviewLoadingSkeleton = memo(
  function DocumentPreviewLoadingSkeleton(): ReactElement {
    const { t } = useLingui()
    return (
      <Box
        aria-label={t`Loading document preview`}
        role='status'
        display='flex'
        direction='column'
        height='full'
        minHeight='none'
        width='full'
        minWidth='none'
        border='left'
        overflow='hidden'>
        <Box
          display='flex'
          align='center'
          justify='between'
          gap='xs'
          shrink={false}
          paddingX='lg'
          paddingY='sm'>
          <Skeleton
            variant='ditheredPulse'
            style={{ height: '1.375rem', width: '60%' }}
          />
          <Skeleton
            variant='ditheredPulse'
            style={{ height: '2.25rem', width: '2.25rem' }}
          />
        </Box>
        <Skeleton
          variant='ditheredPulse'
          style={{
            height: '55%',
            flexShrink: 0,
            borderRadius: 0,
          }}
        />
        <Box
          display='grid'
          gridColumns='two'
          flex='one'
          minHeight='none'
          gap='md'
          padding='md'
          overflow='hidden'
          style={{ gridAutoRows: '1fr' }}>
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton
              key={index}
              variant='ditheredPulse'
              style={{ minHeight: 0 }}
            />
          ))}
        </Box>
      </Box>
    )
  }
)

export interface DocumentResultsContentProps {
  data: {
    documentResults: DocumentTableRow[]
    documentTotalCount: number
    elasticsearchHighlightsMap: Map<string, Highlight[]>
    pageClassificationsMap: Map<string, PageClassification[]>
    drawingRevisionGroupsMap: Map<string, DrawingRevisionGroup[]>
    documentRowsById: Map<string, DocumentTableRow>
    searchedSystemDocuments: SystemDocumentEntry[] | undefined
    isLoadingDocuments: boolean
    documentsLoadError: Error | undefined
  }
  interactions: UseDocumentResultsInteractionsResult
  isActive: boolean
  isLinkedDocsMode: boolean
  layout: {
    leftPaneWidthPercent: number
    setLeftPaneWidthPercent: Dispatch<SetStateAction<number>>
  }
  headerContent?: React.ReactNode
  showCardFilterBar: boolean
  enablePageCardSurfacing: boolean
  tableState: {
    pagination: PaginationState
    setPagination: (
      updater: PaginationState | ((prev: PaginationState) => PaginationState)
    ) => void
    sorting: SortingState
    setSorting: (
      updater: SortingState | ((prev: SortingState) => SortingState)
    ) => void
  }
  viewState: UseDocumentResultsViewStateResult
  viewMode: ViewMode
  isServerPaged?: boolean
  browseAllDisplay?: BrowseAllDisplayState
}

export function DocumentResultsContent({
  data,
  interactions,
  isActive,
  isLinkedDocsMode,
  layout,
  headerContent,
  showCardFilterBar,
  enablePageCardSurfacing,
  tableState,
  viewState,
  viewMode,
  isServerPaged = false,
  browseAllDisplay,
}: DocumentResultsContentProps): ReactElement {
  const { t } = useLingui()
  const {
    documentResults,
    documentTotalCount,
    elasticsearchHighlightsMap,
    pageClassificationsMap,
    drawingRevisionGroupsMap,
    documentRowsById,
    searchedSystemDocuments,
    isLoadingDocuments,
    documentsLoadError,
  } = data
  const {
    handleAddToContext,
    handleRowAuxClick,
    handleRowClick,
    handleRowContextMenu,
  } = interactions
  const { pagination, setPagination, sorting, setSorting } = tableState
  const {
    activeIndex,
    cardFilters,
    commitCardFiltersToUrl,
    contentAreaRef,
    documentPreviewPanelProps,
    filteredDocuments,
    handleCardFiltersChange,
    handleRowFocus,
    handleTableFiltersChange,
    hasActiveFilters,
    isPreviewOpen,
    onViewModeChange,
    onSearchDocumentNavigate,
    setActivePreviewDocumentOverride,
    setActivePreviewPage,
    selectedCardKey,
    setSelectedCardKey,
    selectedRevisionByCardKey,
    setSelectedRevisionByCardKey,
    setActiveIndex,
    setActiveIndexWithoutPreview,
    tableColumns,
    tableColumnUniqueValues,
    tableFilters,
    tableHasActiveFilters,
  } = viewState

  const displayedPagination = useMemo(
    () =>
      browseAllDisplay
        ? { ...pagination, pageIndex: browseAllDisplay.displayedPageIndex }
        : pagination,
    [browseAllDisplay, pagination]
  )
  const displayedSorting = browseAllDisplay
    ? browseAllDisplay.displayedSorting
    : sorting

  const tablePage = useMemo(() => {
    if (viewMode !== 'table') {
      return { filteredCount: documentResults.length, pageRows: [] }
    }
    if (isServerPaged) {
      return {
        filteredCount: documentTotalCount,
        pageRows: documentResults,
      }
    }
    return getDocumentTablePage({
      documents: documentResults,
      columnFilters: tableFilters,
      sorting,
      pagination,
    })
  }, [
    documentResults,
    documentTotalCount,
    isServerPaged,
    pagination,
    sorting,
    tableFilters,
    viewMode,
  ])

  const showFullPaneSkeleton =
    isLoadingDocuments && !hasActiveFilters && documentResults.length === 0
  const hasNoCardResults =
    viewMode === 'card' &&
    !isServerPaged &&
    !hasActiveFilters &&
    documentResults.length === 0 &&
    searchedSystemDocuments?.length === 0
  const hasDocumentsLoadError =
    documentsLoadError !== undefined && documentResults.length === 0
  const showDocumentResultsFooter =
    isLoadingDocuments || (!hasNoCardResults && !hasDocumentsLoadError)
  const showPreviewLayout =
    !hasNoCardResults && !hasDocumentsLoadError && isPreviewOpen
  const { usesFloatingPreview, showFloatingPreview } =
    useFloatingDocumentPreview({
      isActive,
      isOpen: showPreviewLayout,
      onInactive: 'keep',
      onClose: documentPreviewPanelProps.onClose,
    })
  const showSplitPreview = !usesFloatingPreview && showPreviewLayout
  const { leftPaneWidthPercent, setLeftPaneWidthPercent } = layout
  const defaultPreviewLayout = useMemo(
    () => ({
      'document-results': leftPaneWidthPercent,
      'document-preview': 100 - leftPaneWidthPercent,
    }),
    [leftPaneWidthPercent]
  )
  const handlePreviewLayout = useCallback(
    (nextLayout: Record<string, number>) => {
      const nextLeftPaneWidth = nextLayout['document-results']
      if (typeof nextLeftPaneWidth === 'number') {
        setLeftPaneWidthPercent(nextLeftPaneWidth)
      }
    },
    [setLeftPaneWidthPercent]
  )

  const previewPane = (
    <PreviewPane>
      {isLoadingDocuments ? (
        <DocumentPreviewLoadingSkeleton />
      ) : (
        <DocumentPreviewPanel
          {...documentPreviewPanelProps}
          panelBorder={showFloatingPreview ? 'none' : undefined}
          onAddToContext={handleAddToContext}
        />
      )}
    </PreviewPane>
  )

  const resultsFooter = showDocumentResultsFooter ? (
    <DocumentResultsFooter
      activeIndex={activeIndex}
      documentTotalCount={documentTotalCount}
      filteredDocuments={filteredDocuments}
      hasActiveFilters={hasActiveFilters}
      pagination={displayedPagination}
      setActiveIndex={setActiveIndexWithoutPreview}
      setPagination={setPagination}
      tableScrollContainerRef={contentAreaRef}
      tableFilteredCount={tablePage.filteredCount}
      tableHasActiveFilters={tableHasActiveFilters}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      isLoading={isLoadingDocuments}
      isServerPaged={isServerPaged}
    />
  ) : null

  const resultsPane = (
    <Box
      display='flex'
      direction='column'
      flex='one'
      minWidth='none'
      minHeight='none'
      overflow='hidden'>
      {headerContent}
      <Box
        display='flex'
        direction='column'
        flex='one'
        minHeight='none'
        overflow='hidden'
        ref={(node) => {
          contentAreaRef.current = node
        }}
        tabIndex={-1}>
        {showFullPaneSkeleton ? (
          <DocumentResultsLoadingSkeleton />
        ) : hasDocumentsLoadError ? (
          <NoResults>
            <Trans>Unable to load documents</Trans>
          </NoResults>
        ) : !isLoadingDocuments && hasNoCardResults ? (
          <NoResults>
            <Trans>No documents found</Trans>
          </NoResults>
        ) : (
          <Box
            display='flex'
            direction='column'
            flex='one'
            minWidth='none'
            minHeight='none'
            overflow={viewMode === 'card' ? 'yAuto' : 'hidden'}>
            {viewMode === 'card' ? (
              <>
                {showCardFilterBar ? (
                  <DocumentCardFilterBar
                    documents={documentResults}
                    facetOptions={browseAllDisplay?.facetColumnValues}
                    filters={cardFilters}
                    onFiltersChange={handleCardFiltersChange}
                    onFilterCommit={commitCardFiltersToUrl}
                  />
                ) : null}
                {isLoadingDocuments && filteredDocuments.length === 0 ? (
                  <DocumentResultsLoadingSkeleton />
                ) : filteredDocuments.length === 0 ? (
                  <NoResults>
                    {hasActiveFilters
                      ? t`No documents match the current filters`
                      : t`No documents found`}
                  </NoResults>
                ) : (
                  <DocumentResultsList
                    filteredDocuments={filteredDocuments}
                    totalCount={documentTotalCount}
                    filteredCount={filteredDocuments.length}
                    hasActiveFilters={hasActiveFilters}
                    activeIndex={activeIndex}
                    onSelect={(doc) =>
                      onSearchDocumentNavigate(doc, false, isLinkedDocsMode)
                    }
                    onSelectPage={(doc, pageNum, fragmentText) =>
                      onSearchDocumentNavigate(doc, false, isLinkedDocsMode, {
                        pageNum,
                        fragmentText,
                        pageNumber: pageNum,
                      })
                    }
                    onOpenInNewTab={(doc, pageNum) =>
                      onSearchDocumentNavigate(doc, true, isLinkedDocsMode, {
                        pageNumber: pageNum,
                      })
                    }
                    onActiveIndexChange={setActiveIndex}
                    onActivePreviewDocumentChange={
                      setActivePreviewDocumentOverride
                    }
                    onActivePreviewPageChange={setActivePreviewPage}
                    selectedCardKey={selectedCardKey}
                    onSelectedCardKeyChange={setSelectedCardKey}
                    selectedRevisionByCardKey={selectedRevisionByCardKey}
                    onSelectedRevisionByCardKeyChange={
                      setSelectedRevisionByCardKey
                    }
                    highlightsMap={elasticsearchHighlightsMap}
                    pageClassificationsMap={pageClassificationsMap}
                    drawingRevisionGroupsMap={drawingRevisionGroupsMap}
                    documentRowsById={documentRowsById}
                    enablePageCardSurfacing={enablePageCardSurfacing}
                    disableClientPaging={isServerPaged}
                  />
                )}
              </>
            ) : (
              <SearchTableWrapper>
                <GeckoPowerTable
                  cardStyleRows={false}
                  columns={tableColumns}
                  data={tablePage.pageRows}
                  isLoadingData={isLoadingDocuments}
                  footerName='Documents'
                  pagination={displayedPagination}
                  setPagination={setPagination}
                  sorting={displayedSorting}
                  setSorting={setSorting}
                  showTable={true}
                  columnFilters={tableFilters}
                  setColumnFilters={handleTableFiltersChange}
                  onRowClick={handleRowClick}
                  onRowAuxClick={handleRowAuxClick}
                  onRowContextMenu={handleRowContextMenu}
                  onRowFocus={handleRowFocus}
                  showFilterBar={true}
                  disableUrlFilters={true}
                  showFooter={false}
                  showPagination={false}
                  isTableHeaderSticky={true}
                  showColumnConfig={false}
                  cellPadding='.25rem'
                  cellFontSize='smaller'
                  borderSpacing='0'
                  manualFiltering
                  manualSorting
                  manualPagination
                  resetPageOnFilterChange={!isServerPaged}
                  totalRowCount={tablePage.filteredCount}
                  facetedUniqueValuesByColumnId={tableColumnUniqueValues}
                  stableColumnWidths={browseAllDisplay !== undefined}
                  getRowId={(row) => row.id}
                  getRowHtmlAttributes={(row) =>
                    ({
                      'data-row-id': row.id,
                    }) as React.HTMLAttributes<HTMLTableRowElement>
                  }
                />
              </SearchTableWrapper>
            )}
          </Box>
        )}
      </Box>
      {resultsFooter}
    </Box>
  )

  return (
    <Box
      display='flex'
      direction='column'
      flex='one'
      minHeight='none'
      overflow='hidden'>
      <Box
        display='flex'
        direction='row'
        flex='one'
        minHeight='none'
        overflow='hidden'>
        {showSplitPreview ? (
          <ResizablePanelGroup
            orientation='horizontal'
            defaultLayout={defaultPreviewLayout}
            onLayoutChanged={
              isLoadingDocuments ? undefined : handlePreviewLayout
            }>
            <ResizablePanel id='document-results' minSize='20%' maxSize='80%'>
              <ResizablePanelContent>{resultsPane}</ResizablePanelContent>
            </ResizablePanel>
            <ResizableHandle variant='hover' />
            <ResizablePanel id='document-preview' minSize='20%' maxSize='80%'>
              <ResizablePanelContent>{previewPane}</ResizablePanelContent>
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          resultsPane
        )}
      </Box>
      {showFloatingPreview ? (
        <FloatingDocumentPreviewOverlay
          onClose={documentPreviewPanelProps.onClose}>
          {previewPane}
        </FloatingDocumentPreviewOverlay>
      ) : null}
    </Box>
  )
}
